import { Fragment, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Autoplay, FreeMode } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { SwiperClass } from 'swiper/react'
import styled from 'styled-components'

import { toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { useScroll } from '../../hooks'
import { useNavigate } from '../../hooks/useNavigate'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { CenterItem } from '../../components/common'
import { Text } from '../../components/text'
import { HomeBanner } from '../../components/banner'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { ContentItem } from '../contents/components'

import { Color } from '../../theme/color'

import { ReactComponent as NotifyIcon } from '../../assets/gui/notify_56.svg'
import { ReactComponent as ForwardIcon } from '../../assets/ico/common/arrow/right/arrow_right_black_medium.svg'

import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import 'swiper/css/autoplay'

export const Home = observer(() => {
  const {
    commonStore,
    homeStore,
    authStore: { userInfo, checkLogin, getUserInfo },
  } = useContext(StoresContext)
  const mainStore = homeStore.mainStore

  const [activeBannerIndex, setActiveBannerIndex] = useState<number>(0)

  const location = useLocation()
  const navigate = useNavigate()
  const [isScrollHeader, onScrollHeader] = useScroll({ breakPoint: window.innerWidth * 1.12 - 46 })

  useEffect(() => {
    commonStore.setShowTab(true)
    loadMain()
  }, [])

  useEffect(() => {
    if (location.pathname.includes('/home')) commonStore.setActiveTab('home')
  }, [location])

  const loadMain = async () => {
    try {
      if (!userInfo) {
        const isLogin = await checkLogin()
        if (isLogin) {
          await getUserInfo()
        }
      }
      await mainStore.load(undefined, true)
    } catch (err) {
      toast('페이지를 불러오는데 실패했습니다.', err)
    }
  }

  if (!mainStore.content) {
    return (
      <Wrap>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </Wrap>
    )
  }

  const homeBanner = mainStore.content?.homeBanners[0]

  const navigateHomeBanner = () => {
    homeBanner?.targetUrl && navigate(homeBanner?.targetUrl)
  }

  const rollingBannerChange = ({ realIndex }: SwiperClass) => {
    setActiveBannerIndex(realIndex || 0)
  }

  // const getRevenueRate = () => {
  //   const revenueRate = mainStore.content?.sellHistory?.revenueRate
  //   if (!revenueRate) return '-'
  //   const revenueRateString = amountString(String(revenueRate), false, 2)
  //   return revenueRate > 0 ? `+ ${revenueRateString}%` : `${revenueRateString.replace('-', '- ')}%`
  // }

  return (
    <Wrap>
      <Header title="" type="MAIN" fixed={true} isScrollEvent={true} isScrolling={isScrollHeader} />
      <Content backgroundColor="gray_1" isPullEvent={true} onScroll={onScrollHeader} onPull={loadMain}>
        <HomeBanner onClick={navigateHomeBanner}>
          <img src={homeBanner?.imageUri} alt={homeBanner?.title} />
        </HomeBanner>
        <Container>
          <FirstGuideContainer to="/notice/1">
            <div>
              <div className="notify-icon-box">
                <NotifyIcon />
              </div>
              <div className="text-box">
                <Text size={16} weight={500}>
                  <Text size={16} weight={500} color="primary_5">
                    예술품 청약
                  </Text>
                  이 처음이세요?
                </Text>
                <Text size={14} weight={500} color="text_secondary">
                  가이드 읽으러 가기
                </Text>
              </div>
            </div>
          </FirstGuideContainer>
          <SwiperWrap>
            {mainStore.content?.rollingBanners && (
              <Swiper
                modules={[Autoplay]}
                loop={true}
                autoplay={{ delay: 3000, disableOnInteraction: false }}
                onActiveIndexChange={rollingBannerChange}
              >
                {mainStore.content.rollingBanners.map(({ id, imageUri, title, targetUrl }) => {
                  if (!imageUri || !targetUrl) {
                    return <Fragment key={id} />
                  }

                  const route = () => navigate(targetUrl)

                  return (
                    <SwiperSlide key={id}>
                      <BannerCard onClick={route}>
                        <img src={imageUri} alt={title} />
                      </BannerCard>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            )}
            <BannerCountBox>
              <Text size={12} weight={500} color="text_white" style={{ lineHeight: '20.5px' }}>
                {activeBannerIndex + 1} /{' '}
                {mainStore.content?.rollingBanners.filter(({ imageUri, targetUrl }) => imageUri && targetUrl).length}
              </Text>
            </BannerCountBox>
          </SwiperWrap>
          <ContentsContainer>
            <ContentTitle>
              <Link to="/contents">
                <Text size={20} weight={600}>
                  콘텐츠 더보기
                </Text>
                <ForwardIcon />
              </Link>
            </ContentTitle>
            <ContentList>
              <Swiper
                freeMode={{ enabled: true, momentum: false }}
                modules={[FreeMode]}
                spaceBetween={8}
                slidesPerView="auto"
              >
                {mainStore.content?.articles.map(({ id, title, imageUri }) => {
                  return (
                    <SwiperSlide key={id}>
                      <ContentItem
                        title={title ?? '-'}
                        imgUri={imageUri ?? ''}
                        href={`/contents/${id}`}
                        width={184}
                        line={1}
                      />
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </ContentList>
          </ContentsContainer>
          {/* TODO: 추후 주석 해제 */}
          {/* <HistoryContainer>
              <ContentTitle onClick={() => history.push('/market', { listType: LIST_MARKET.SALE_OFF })}>
                <Text size={18} weight={600}>
                  이전 매각 히스토리
                </Text>
                <ForwardIcon />
              </ContentTitle>
              <HistoryCardContainer>
                <Card $padding="16px 24px" $radius={8} $background="white" $shadow="0 4px 6px 0 rgba(0, 0, 0, 0.05)">
                  <GraphIcon />
                  <div>
                    <Text size={12} color="text_secondary">
                      평균 수익률
                    </Text>
                    <Text size={18} weight={600} color="primary_5">
                      {getRevenueRate()}
                    </Text>
                  </div>
                </Card>
                <Card $padding="16px 24px" $radius={8} $background="white" $shadow="0 4px 6px 0 rgba(0, 0, 0, 0.05)">
                  <ClockIcon />
                  <div>
                    <Text size={12} color="text_secondary">
                      평균 보유기간
                    </Text>
                    <Text size={18} weight={600} color="primary_5">
                      {mainStore.content?.sellHistory?.retentionDate || '--'}일
                    </Text>
                  </div>
                </Card>
              </HistoryCardContainer>
            </HistoryContainer> */}
        </Container>
      </Content>
    </Wrap>
  )
})

const SpinnerContainer = styled(CenterItem)`
  width: 100%;
  height: 100%;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px 0 54px;
`

const BannerCard = styled.div`
  overflow: hidden;
  width: 100%;
  aspect-ratio: 1 / 0.22;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const SwiperWrap = styled.div`
  overflow: hidden;
  position: relative;
  width: calc(100% - 16px * 2);
  margin: 0 16px;
  border-radius: 8px;
`

const BannerCountBox = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 1;
  padding: 2px 10px;
  border-radius: 50vh;
  background-color: rgba(0, 0, 0, 0.6);
`

const FirstGuideContainer = styled(Link)`
  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    width: 100%;
    margin: 0 16px;
  }
  .notify-icon-box {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background-color: ${Color.white};
  }
  .text-box {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
`

const ContentTitle = styled.div`
  display: flex;
  align-items: center;
`

// const HistoryCardContainer = styled.div`
//   display: grid;
//   grid-template-columns: 1fr 1fr;
//   column-gap: 16px;
//   > div {
//     display: flex;
//     align-items: center;
//     gap: 8px;
//     & > div {
//       display: flex;
//       flex-direction: column;
//       gap: 2px;
//     }
//   }
// `

// const HistoryContainer = styled.div`
//   display: flex;
//   flex-direction: column;
//   gap: 16px;
//   margin: 0 16px;
// `

const ContentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${ContentTitle} {
    padding-left: 16px;
    > a {
      display: flex;
      align-items: center;
    }
  }
`

const ContentList = styled.div`
  display: flex;
  gap: 8px;
  position: relative;
  > .swiper {
    padding: 0 16px;
    .swiper-slide {
      width: fit-content;
    }
  }
`
